import type {
  FmMapFormFlowDialogDescriptor,
  FmMapFilterDescriptor,
  FmMapFilterOption,
  FmMapPanelDescriptor,
  FmMapRadarDescriptor,
  FmMapWorkspaceMode,
  FmMapWorkspaceStoryDescriptor,
} from "./FmMapWorkspaceRoot.types";
import type {
  FmFormFlowActionDescriptor,
  FmFormFlowBlockDescriptor,
  FmFormFlowEvent,
  FmFormFlowPageBase,
  FmFormFlowPageDescriptor,
  FmFormFlowStageStateCopy,
} from "./FmFormFlowPage.types";
import type { FmConfirmationCopyDescriptor } from "./FmConfirm";
import type { FmEvidenceDescriptor } from "./FmEvidence.types";

interface FmMapWorkspaceStoryInput {
  ownerQuestion: string;
  literalAnswer: string;
  stateAnswers: {
    loading: string;
    empty: string;
    locked: string;
    error: string;
  };
}

const MODE_IDENTITY = {
  find: {
    allowedTransitions: ["orient"],
    conversionRole: "find",
    selectionKinds: ["none"],
    requiredServedFields: [
      "search query",
      "property identity",
      "boundary reference",
    ],
    requiredProvenance: ["property source", "retrieved date"],
  },
  orient: {
    allowedTransitions: ["find", "inspect", "risk", "refine"],
    conversionRole: "orient",
    selectionKinds: ["property"],
    requiredServedFields: [
      "property identity",
      "area",
      "mapped stand count",
      "cutting-ready stand count",
      "dominant species",
      "served total volume or volume range",
    ],
    requiredProvenance: ["source", "as-of date", "revision"],
  },
  inspect: {
    allowedTransitions: ["orient", "inspect", "risk", "refine"],
    conversionRole: "inspect",
    selectionKinds: ["stand", "campaign", "plot"],
    requiredServedFields: [
      "stand identity",
      "area",
      "dominant species",
      "volume range",
    ],
    requiredProvenance: ["source", "as-of date", "revision"],
  },
  risk: {
    allowedTransitions: ["orient", "inspect", "risk"],
    conversionRole: "assess-risk",
    selectionKinds: ["risk"],
    requiredServedFields: [
      "advisory identity",
      "scope",
      "effective time",
      "entitlement",
    ],
    requiredProvenance: ["advisory source", "observed date", "revision"],
  },
  refine: {
    allowedTransitions: ["orient", "inspect", "risk"],
    conversionRole: "refine",
    selectionKinds: ["none", "property", "stand", "campaign", "plot"],
    requiredServedFields: [
      "visible stand count",
      "total stand count",
      "active semantic filters",
    ],
    requiredProvenance: ["source revision", "property boundary source"],
  },
} as const satisfies Record<
  FmMapWorkspaceMode,
  Pick<
    FmMapWorkspaceStoryDescriptor,
    | "allowedTransitions"
    | "conversionRole"
    | "requiredServedFields"
    | "requiredProvenance"
  > & {
    selectionKinds: readonly (
      | "none"
      | "property"
      | "stand"
      | "campaign"
      | "plot"
      | "risk"
    )[];
  }
>;

const MODE_FOR_SELECTION = {
  none: "find",
  property: "orient",
  stand: "inspect",
  campaign: "inspect",
  plot: "inspect",
  risk: "risk",
} as const;

const MOBILE_ORDER = ["mode-answer", "map"] as const;

const STATE_POLICY = {
  ready: { retainsServedAnswer: true },
  loading: { retainsServedAnswer: false },
  empty: { retainsServedAnswer: false },
  partial: { retainsServedAnswer: true },
  stale: { retainsServedAnswer: true },
  locked: { retainsServedAnswer: false },
  error: { retainsServedAnswer: false },
} as const;

function stateBehavior(
  literalAnswer: string,
  answers: FmMapWorkspaceStoryInput["stateAnswers"],
) {
  return {
    ready: { answer: literalAnswer, ...STATE_POLICY.ready },
    loading: { answer: answers.loading, ...STATE_POLICY.loading },
    empty: { answer: answers.empty, ...STATE_POLICY.empty },
    partial: { answer: literalAnswer, ...STATE_POLICY.partial },
    stale: { answer: literalAnswer, ...STATE_POLICY.stale },
    locked: { answer: answers.locked, ...STATE_POLICY.locked },
    error: { answer: answers.error, ...STATE_POLICY.error },
  } as const;
}

/** Define one complete map-mode recipe without allowing page-owned layout. */
export function defineMapWorkspaceStory(
  mode: FmMapWorkspaceMode,
  input: FmMapWorkspaceStoryInput,
): FmMapWorkspaceStoryDescriptor {
  const { stateAnswers, ...copy } = input;
  return {
    ...copy,
    dominantBeat: "map",
    requiredServedFields: MODE_IDENTITY[mode].requiredServedFields,
    requiredProvenance: MODE_IDENTITY[mode].requiredProvenance,
    allowedTransitions: MODE_IDENTITY[mode].allowedTransitions,
    stateBehavior: stateBehavior(input.literalAnswer, stateAnswers),
    mobileOrder: MOBILE_ORDER,
    conversionRole: MODE_IDENTITY[mode].conversionRole,
  };
}

export const MAP_WORKSPACE_MODE_IDENTITY = MODE_IDENTITY;
export const MAP_WORKSPACE_MODE_FOR_SELECTION = MODE_FOR_SELECTION;
export const MAP_WORKSPACE_MOBILE_ORDER = MOBILE_ORDER;
export const MAP_WORKSPACE_STATE_POLICY = STATE_POLICY;

/**
 * The workspace's served evidence as a map panel: a prose section holding the
 * provenance element, its test ids hanging off `map-workspace-panel`.
 */
export function defineMapEvidencePanel(input: {
  id: string;
  title: string;
  evidence: FmEvidenceDescriptor;
}): Extract<FmMapPanelDescriptor, { kind: "section" }> {
  return {
    id: input.id,
    kind: "section",
    title: input.title,
    section: {
      id: `${input.id}-section`,
      arrangement: "prose",
      state: { status: "ready" },
      testId: "map-workspace-panel",
      slots: { content: [{ element: "provenance", evidence: input.evidence }] },
    },
  };
}

interface FmMapOneOfFilterInput {
  id: string;
  sourceId: string;
  label: string;
  options: readonly FmMapFilterOption[];
  selectedValues: readonly string[];
}

/** Define a one-of filter and discard selections that the served options no longer contain. */
export function defineMapOneOfFilter(
  input: FmMapOneOfFilterInput,
): FmMapFilterDescriptor {
  return {
    ...input,
    operator: "one-of",
    selectedValues: input.selectedValues.filter((value) =>
      input.options.some((option) => option.value === value)
    ),
  };
}

/** Map-owned inert radar state for spatial workspaces that do not offer radar. */
export function defineUnavailableMapRadar(
  propertyId: string | null,
  reason: string,
  copy: FmMapRadarDescriptor["copy"],
): FmMapRadarDescriptor {
  return {
    propertyId,
    state: "empty",
    frames: [],
    affectedStandCount: { status: "unavailable", reason },
    maximumIntensity: { status: "unavailable", reason },
    accumulatedRain: { status: "unavailable", reason },
    noDataReason: reason,
    error: null,
    summaryError: null,
    reload: () => undefined,
    copy,
  };
}

export interface FmMapSingleStepDialogInput {
  id: string;
  title: string;
  description: string;
  closeLabel: string;
  size: "md" | "lg";
  stageLabel: string;
  block: FmFormFlowBlockDescriptor;
  actions: readonly FmFormFlowActionDescriptor[];
  state: "ready" | "locked";
  stateLabel: string;
  disabledReason?: string;
  requiredServedFields: readonly string[];
  requiredProvenance: readonly string[];
  primaryAction: string;
  conversionRole:
    | "input"
    | "orientation"
    | "evidence"
    | "declaration"
    | "confirmation"
    | "handoff";
  stateCopy: FmFormFlowStageStateCopy;
  onEvent: (event: FmFormFlowEvent) => void;
  closeConfirmation?: FmConfirmationCopyDescriptor;
}

export interface FmMapSingleStageDialogInput
  extends Omit<FmMapSingleStepDialogInput, "block"> {
  blocks: readonly [
    FmFormFlowBlockDescriptor,
    ...FmFormFlowBlockDescriptor[],
  ];
  dominantBeatId: string;
}

/** Define one governed FormFlow stage without repeating its semantic shell in a map route. */
export function defineMapSingleStageDialog(
  input: FmMapSingleStageDialogInput,
): FmMapFormFlowDialogDescriptor {
  const blockIds = input.blocks.map(({ id }) => id);
  if (!blockIds.includes(input.dominantBeatId)) {
    throw new Error("Map dialog dominant beat must reference one served block.");
  }
  const flowBase: FmFormFlowPageBase = {
      id: input.id,
      ariaLabel: input.title,
      title: input.title,
      description: input.description,
      stateLabel: input.stateLabel,
      navigation: {
        kind: "linear",
        ariaLabel: input.title,
        activeId: input.id,
        invalidationMessage: input.description,
        items: [{
          id: input.id,
          label: input.stageLabel,
          status: input.state === "ready" ? "current" : "blocked",
          disabledReason: input.disabledReason,
          blockIds,
          story: {
            kind: "story-transition",
            ownerQuestion: input.title,
            literalAnswer: input.description,
            dominantBeat: input.dominantBeatId,
            requiredServedFields: input.requiredServedFields,
            requiredProvenance: input.requiredProvenance,
            primaryAction: input.primaryAction,
            expertReveal: input.description,
            transition: input.description,
            state: input.state,
            stateCopy: input.stateCopy,
            mobileOrder: blockIds,
            conversionRole: input.conversionRole,
          },
        }],
      },
      blocks: input.blocks,
      actions: input.actions,
      onEvent: input.onEvent,
  };
  /* A dialog lock is a PRECONDITION, not a paid boundary, so it names itself and
     its reason and carries no unlock preview. */
  const flow: FmFormFlowPageDescriptor = input.state === "ready"
    ? { ...flowBase, state: "ready" }
    : {
        ...flowBase,
        state: "locked",
        locked: {
          title: input.stateLabel,
          /* Never the title again: with no served reason the stage's own locked
             copy explains the boundary, instead of printing one sentence twice. */
          description: input.disabledReason ?? input.stateCopy.locked,
        },
      };
  return {
    kind: "form-flow",
    id: input.id,
    open: true,
    title: input.title,
    closeLabel: input.closeLabel,
    closeConfirmation: input.closeConfirmation,
    size: input.size,
    flow,
  };
}

/** Define one compact governed FormFlow dialog without repeating its semantic shell in a route. */
export function defineMapSingleStepDialog(
  input: FmMapSingleStepDialogInput,
): FmMapFormFlowDialogDescriptor {
  return defineMapSingleStageDialog({
    ...input,
    blocks: [input.block],
    dominantBeatId: input.block.id,
  });
}
